import { computed, nextTick, ref } from 'vue';
import { getOneEmployeesOffer } from '@/api/offers';
import {
    IContestCashbackGoal,
    IContestCashbackOffer,
    IContestSpecialPeriod,
} from '@/components_v3/affiliate/contest/entities/cashback-offer/model/types';
import {
    bonusesKey,
    cloneDraft,
    draftFromOffer,
    emptyDraft,
    specialPeriodKey,
} from '@/components_v3/affiliate/contest/model/draft.lib';
import {
    buildPatchPayload,
    buildPostOfferItem,
    getContestOfferSaveValidationError,
} from '@/components_v3/affiliate/contest/model/payload.lib';
import type { ContestActiveCard, ContestEditSnapshot, ContestOfferDraft } from '@/components_v3/affiliate/contest/model/types';
import {
    createAffiliateCashbackOffer,
    deleteAffiliateCashbackOffer,
    updateAffiliateCashbackOffer,
} from '@/components_v3/affiliate/contest/shared/api/contest-cashback.api';
import { mapContestOfferGoalsForAdd } from '@/components_v3/affiliate/contest/shared/lib/map-goals.lib';
import { isContestSavedPlanCleared } from '@/components_v3/affiliate/contest/shared/lib/parse-offer-money.lib';
import { showErrorModal, showServerError } from '@/utils';

const SAVE_ERROR_MESSAGE = 'Не удалось сохранить оффер';
const NO_CHANGES_ERROR_MESSAGE = 'Нет изменений для сохранения';
const DELETE_ERROR_MESSAGE = 'Не удалось удалить оффер';
const GOALS_LOAD_ERROR_MESSAGE = 'Не удалось загрузить цели оффера';
const OFFER_ALREADY_IN_MONTH_MESSAGE = 'Оффер уже добавлен в этот месяц';

interface UseContestOfferEditorOptions {
    affiliateId: { value: string | number | undefined };
    month: { value: string };
    offers: { value: IContestCashbackOffer[] };
    reloadOffers: () => Promise<void>;
}

function clearGoalsPeriodBonus(goals: IContestCashbackGoal[]): IContestCashbackGoal[] {
    return goals.map((goal) => ({ ...goal, periodBonus: null }));
}

export function useContestOfferEditor(options: UseContestOfferEditorOptions) {
    const activeCard = ref<ContestActiveCard>(null);
    const draft = ref<ContestOfferDraft>(emptyDraft());
    const snapshot = ref<ContestEditSnapshot | null>(null);
    const isSubmitting = ref(false);
    const isGoalsLoading = ref(false);

    const isEditingOffer = (offerId: number): boolean =>
        activeCard.value?.kind === 'edit' && activeCard.value.offerId === offerId;

    const isAddActiveInSection = (createdByAffiliate: boolean): boolean =>
        activeCard.value?.kind === 'add' && activeCard.value.createdByAffiliate === createdByAffiliate;

    const existingOfferIds = computed(() => options.offers.value.map((offer) => offer.offerId));

    const canSave = computed(() => {
        if (isSubmitting.value || isGoalsLoading.value) {
            return false;
        }

        if (!activeCard.value) {
            return false;
        }

        if (!draft.value.offerId) {
            return false;
        }

        if (
            activeCard.value.kind === 'edit'
            && isContestSavedPlanCleared(snapshot.value?.hadSavedPlan ?? false, draft.value.plan)
        ) {
            return false;
        }

        return true;
    });

    function resetEditor(): void {
        activeCard.value = null;
        draft.value = emptyDraft();
        snapshot.value = null;
    }

    function startAdd(createdByAffiliate: boolean): void {
        activeCard.value = { kind: 'add', createdByAffiliate };
        draft.value = emptyDraft();
        snapshot.value = null;
    }

    function syncEditSnapshot(nextDraft: ContestOfferDraft, hadSavedPlan: boolean): void {
        snapshot.value = {
            draft: cloneDraft(nextDraft),
            hadSavedPlan,
            initialBonusesKey: bonusesKey(nextDraft.goals),
            initialSpecialPeriodEnabled: nextDraft.specialPeriodEnabled,
            initialSpecialPeriodKey: specialPeriodKey(nextDraft.specialPeriod),
        };
    }

    async function loadGoalsForEditIfEmpty(offerId: number, hadSavedPlan: boolean): Promise<void> {
        if (draft.value.goals.length > 0) {
            return;
        }

        isGoalsLoading.value = true;

        try {
            const offerData = await getOneEmployeesOffer(offerId);

            if (!isEditingOffer(offerId)) {
                return;
            }

            draft.value.goals = mapContestOfferGoalsForAdd(offerData);
            syncEditSnapshot(draft.value, hadSavedPlan);
        } catch (err) {
            if (isEditingOffer(offerId)) {
                draft.value.goals = [];
                showServerError(err, GOALS_LOAD_ERROR_MESSAGE);
            }
        } finally {
            if (isEditingOffer(offerId)) {
                isGoalsLoading.value = false;
            }
        }
    }

    async function startEdit(offer: IContestCashbackOffer): Promise<void> {
        activeCard.value = { kind: 'edit', offerId: offer.offerId };
        const nextDraft = draftFromOffer(offer);
        const hadSavedPlan = offer.customGoal != null;
        draft.value = nextDraft;
        syncEditSnapshot(nextDraft, hadSavedPlan);
        await loadGoalsForEditIfEmpty(offer.offerId, hadSavedPlan);
    }

    function cancel(): void {
        resetEditor();
    }

    function resetSpecialPeriodState(): void {
        draft.value.specialPeriodEnabled = false;
        draft.value.specialPeriod = null;
        draft.value.goals = clearGoalsPeriodBonus(draft.value.goals);
    }

    async function onOfferIdChange(offerId: number | null): Promise<void> {
        const requestId = offerId;
        draft.value.offerId = offerId;
        draft.value.plan = null;
        resetSpecialPeriodState();

        if (!offerId) {
            draft.value.offerName = null;
            draft.value.goals = [];
            return;
        }

        isGoalsLoading.value = true;

        try {
            const offerData = await getOneEmployeesOffer(offerId);

            if (draft.value.offerId !== requestId) {
                return;
            }

            draft.value.offerName = offerData?.name ?? null;
            draft.value.goals = mapContestOfferGoalsForAdd(offerData);
        } catch (err) {
            if (draft.value.offerId === requestId) {
                draft.value.offerName = null;
                draft.value.goals = [];
                showServerError(err, GOALS_LOAD_ERROR_MESSAGE);
            }
        } finally {
            if (draft.value.offerId === requestId) {
                isGoalsLoading.value = false;
            }
        }
    }

    function updatePlan(plan: number | null): void {
        draft.value.plan = plan;
    }

    function updateGoals(goals: IContestCashbackGoal[]): void {
        draft.value.goals = goals;
    }

    function updateSpecialPeriodEnabled(enabled: boolean): void {
        draft.value.specialPeriodEnabled = enabled;

        if (!enabled) {
            draft.value.specialPeriod = null;
            draft.value.goals = clearGoalsPeriodBonus(draft.value.goals);
        }
    }

    function updateSpecialPeriod(period: IContestSpecialPeriod | null): void {
        draft.value.specialPeriod = period;
    }

    async function save(): Promise<void> {
        if (!activeCard.value || !options.affiliateId.value) {
            return;
        }

        if (isSubmitting.value || isGoalsLoading.value) {
            return;
        }

        if (!draft.value.offerId) {
            return;
        }

        const saveValidationError = getContestOfferSaveValidationError(draft.value, {
            hadSavedPlan: activeCard.value.kind === 'edit'
                ? (snapshot.value?.hadSavedPlan ?? false)
                : false,
        });

        if (saveValidationError) {
            showErrorModal(saveValidationError);
            return;
        }

        if (!canSave.value) {
            return;
        }

        const affiliateId = options.affiliateId.value;
        const month = options.month.value;
        const offerId = draft.value.offerId;

        if (!offerId) {
            return;
        }

        // Уникальность offer_id на месяц (как в contest-service / ЛК): блоки «Мой выбор» и «Суперкешбек» не пересекаются.
        if (activeCard.value.kind === 'add' && existingOfferIds.value.includes(offerId)) {
            showErrorModal(OFFER_ALREADY_IN_MONTH_MESSAGE);
            return;
        }

        isSubmitting.value = true;

        try {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            await nextTick();

            if (activeCard.value.kind === 'add') {
                await createAffiliateCashbackOffer(affiliateId, {
                    month,
                    created_by_affiliate: activeCard.value.createdByAffiliate,
                    offers: [buildPostOfferItem(draft.value)],
                });
            } else {
                const payload = buildPatchPayload(draft.value, snapshot.value, month);

                if (!payload) {
                    showErrorModal(NO_CHANGES_ERROR_MESSAGE);
                    return;
                }

                await updateAffiliateCashbackOffer(affiliateId, offerId, payload);
            }

            resetEditor();
            await options.reloadOffers();
        } catch (err) {
            showServerError(err, SAVE_ERROR_MESSAGE);
        } finally {
            isSubmitting.value = false;
        }
    }

    async function deleteOffer(offer: IContestCashbackOffer): Promise<boolean> {
        if (!options.affiliateId.value) {
            return false;
        }

        isSubmitting.value = true;

        try {
            await deleteAffiliateCashbackOffer(
                options.affiliateId.value,
                offer.offerId,
                options.month.value,
            );

            if (isEditingOffer(offer.offerId)) {
                resetEditor();
            }

            await options.reloadOffers();
            return true;
        } catch (err) {
            showServerError(err, DELETE_ERROR_MESSAGE);
            return false;
        } finally {
            isSubmitting.value = false;
        }
    }

    return {
        activeCard,
        draft,
        isSubmitting,
        isGoalsLoading,
        canSave,
        isEditingOffer,
        isAddActiveInSection,
        startAdd,
        startEdit,
        cancel,
        onOfferIdChange,
        updatePlan,
        updateGoals,
        updateSpecialPeriodEnabled,
        updateSpecialPeriod,
        save,
        deleteOffer,
        resetEditor,
    };
}

export type { ContestOfferDraft } from '@/components_v3/affiliate/contest/model/types';
